import React, { useState, useEffect, useMemo } from 'react';
import { connect } from 'react-redux';
import { Actions as ADMINACTIONS } from '../../../store/actions/adminActions';
import Breadcrumb from '../../Common/Breadcrumb';
import { Button } from '../../ui/button';
import _ from 'lodash';
import Select from 'src/components/ui/BrandSelect';
import { adminPortalSelect } from '../../utils/ReactSelectStyles';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../../ui/tooltip';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../ui/table';
import { Spinner } from '../../ui/spinner';

const items = [{ label: 'Audit Logs', link: '/admin/auditLogs' }];
// const boxStyles = { display: 'flex', flexDirection: 'row', justifyContent: 'space-between' };

const formatCellVal = (v) => {
  if (v == null) return '<empty>';
  if (Array.isArray(v)) {
    return v
      .map((x) => (x !== null && typeof x === 'object' ? JSON.stringify(x) : String(x)))
      .join(', ');
  }
  if (typeof v === 'object') return JSON.stringify(v);
  return String(v);
};

// View Details Modal
const ViewDetailsModal = ({ open, handleClose, data }) => {
  return (
    <Dialog open={open} onOpenChange={(isOpen) => { if (!isOpen) handleClose(); }}>
      <DialogContent className='max-w-2xl max-h-[85vh] overflow-y-auto p-0 gap-0'>
        <DialogHeader className='px-6 sm:px-7'>
          <DialogTitle className='text-foreground'>View Details</DialogTitle>
        </DialogHeader>
        <div className='px-6 pb-6 sm:px-7 sm:pb-7 space-y-5'>
          <div className='space-y-1'>
            <h4 className='text-sm font-semibold text-foreground'>Author</h4>
            <p className='text-sm text-muted-foreground'>{data?.authorName || '<empty>'}</p>
            <p className='text-sm text-muted-foreground'>{data?.authorID || '<empty>'}</p>
          </div>
          <hr className='border-border' />
          <div className='space-y-1'>
            <h4 className='text-sm font-semibold text-foreground'>Item ID</h4>
            <p className='text-sm text-muted-foreground break-all'>{data?.itemId || '<empty>'}</p>
          </div>
          <hr className='border-border' />
          <div className='space-y-1'>
            <h4 className='text-sm font-semibold text-foreground'>Change</h4>
            {data?.change === 'Added' && (
              <span className='inline-flex items-center rounded-full border border-success/30 bg-success-bg px-2.5 py-0.5 text-xs font-medium text-success'>
                Added
              </span>
            )}
            {data?.change === 'Deleted' && (
              <span className='inline-flex items-center rounded-full border border-danger/30 bg-danger-bg px-2.5 py-0.5 text-xs font-medium text-danger'>
                Deleted
              </span>
            )}
            {data?.change === 'Updated' && (
              <span className='inline-flex items-center rounded-full border border-warning/30 bg-warning-bg px-2.5 py-0.5 text-xs font-medium text-warning'>
                Updated
              </span>
            )}
          </div>
          <hr className='border-border' />
          <div className='space-y-1'>
            <h4 className='text-sm font-semibold text-foreground'>Table</h4>
            <p className='text-sm text-muted-foreground'>{data?.table || '<empty>'}</p>
          </div>
          <hr className='border-border' />
          <div className='space-y-2'>
            <h4 className='text-sm font-semibold text-foreground'>Difference</h4>
            <div className='rounded-lg border border-border overflow-hidden'>
              {data?.change === 'Updated' && (
                <Table>
                  <TableHeader>
                    <TableRow className='border-border hover:bg-transparent'>
                      <TableHead className='text-muted-foreground'>Field</TableHead>
                      <TableHead className='text-muted-foreground'>Old Value</TableHead>
                      <TableHead className='text-muted-foreground'>New Value</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {Object.entries(data.diff || {}).map(([field, diffEntry]) => {
                      let node = diffEntry;
                      while (
                        node &&
                        typeof node === 'object' &&
                        !Array.isArray(node) &&
                        !('__old' in node && '__new' in node)
                      ) {
                        const keys = Object.keys(node);
                        if (keys.length === 1) {
                          node = node[keys[0]];
                        } else {
                          break;
                        }
                      }
                      const oldRaw =
                        node && node.__old !== undefined
                          ? node.__old
                          : diffEntry && diffEntry.__old !== undefined
                            ? diffEntry.__old
                            : undefined;
                      const newRaw =
                        node && node.__new !== undefined
                          ? node.__new
                          : diffEntry && diffEntry.__new !== undefined
                            ? diffEntry.__new
                            : undefined;
                      return (
                        <TableRow key={field} className='border-border'>
                          <TableCell className='text-foreground'>{field}</TableCell>
                          <TableCell className='text-muted-foreground text-xs break-all'>{formatCellVal(oldRaw)}</TableCell>
                          <TableCell className='text-muted-foreground text-xs break-all'>{formatCellVal(newRaw)}</TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
              {data?.change === 'Added' && (
                <Table>
                  <TableHeader>
                    <TableRow className='border-border hover:bg-transparent'>
                      <TableHead className='text-muted-foreground'>Field</TableHead>
                      <TableHead className='text-muted-foreground'>Value</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {Object.keys(data?.diff || {}).map((key) => (
                      <TableRow key={key} className='border-border'>
                        <TableCell className='text-foreground'>{key}</TableCell>
                        <TableCell className='text-muted-foreground text-xs break-all'>{formatCellVal(data.diff[key])}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
              {data?.change === 'Deleted' && (
                <Table>
                  <TableHeader>
                    <TableRow className='border-border hover:bg-transparent'>
                      <TableHead className='text-muted-foreground'>Field</TableHead>
                      <TableHead className='text-muted-foreground'>Value</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {Object.keys(data?.diff || {}).map((key) => (
                      <TableRow key={key} className='border-border'>
                        <TableCell className='text-foreground'>{key}</TableCell>
                        <TableCell className='text-muted-foreground text-xs break-all'>{formatCellVal(data.diff[key])}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </div>
          </div>
          <hr className='border-border' />
          <div className='space-y-1'>
            <h4 className='text-sm font-semibold text-foreground'>Date Modified</h4>
            <p className='text-sm text-muted-foreground'>{data?.datemodified || '<empty>'}</p>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Audit Logs component
const AuditLogs = ({ getAuditLogs, auditLogs, showLoader }) => {
  const [auditLogsData, setAuditLogsData] = useState([]);
  // default to the current month-year
  const now = new Date();
  const currentMonthYear = `${now.toLocaleString('default', { month: 'long' })}-${now.getFullYear()}`;
  const [selectedPeriod, setSelectedPeriod] = useState(currentMonthYear);
  const [viewDetailsModal, setViewDetailsModal] = useState(false);
  const [viewDetailsModalData, setViewDetailsModalData] = useState(null);

  // filter state
  const [filterTable, setFilterTable] = useState(null);
  const [filterAuthor, setFilterAuthor] = useState(null);
  const [searchText, setSearchText] = useState('');

  // Periods to select
  const periods = [];
  const startYear = 2025;
  const startMonth = 0; // January
  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth();
  for (let year = startYear; year <= currentYear; year++) {
    const monthLimit = year === currentYear ? currentMonth : 11;
    for (let month = year === startYear ? startMonth : 0; month <= monthLimit; month++) {
      let date = new Date(year, month);
      periods.push({
        label: `${date.toLocaleString('default', { month: 'long' })}-${date.getFullYear()}`,
        value: `${date.toLocaleString('default', { month: 'long' })}-${date.getFullYear()}`,
      });
    }
  }

  // Fetch the audit logs data
  useEffect(() => {
    setAuditLogsData([]);
    (async () => setAuditLogsData(await getAuditLogs(selectedPeriod)))();
  }, [getAuditLogs, selectedPeriod]);

  // derive filter options from the raw logs
  const filterOptions = useMemo(() => {
    const entries = auditLogsData?.logs?.data || [];
    return {
      tables: _.uniq(entries.map((e) => JSON.parse(e.message).table)).map((t) => ({ label: t, value: t })),
      authors: _.uniq(
        entries.map((e) => {
          const a = JSON.parse(e.message).author;
          return a.substring(0, a.indexOf('<'));
        }),
      ).map((a) => ({ label: a, value: a })),
      itemIds: _.uniq(entries.map((e) => JSON.parse(e.message).itemId)).map((id) => ({ label: id, value: id })),
    };
  }, [auditLogsData]);

  // filter the list before rendering
  const displayedLogs = useMemo(() => {
    const entries = auditLogsData?.logs?.data || [];

    const hay = searchText.toLowerCase().trim();

    // 1) Filter
    const filtered = entries.filter((e) => {
      const msg = JSON.parse(e.message);
      const authorName = msg.author.substring(0, msg.author.indexOf('<'));

      // dropdown filters:
      if (filterTable && msg.table !== filterTable.value) return false;
      if (filterAuthor && authorName !== filterAuthor.value) return false;

      // global text search across all fields:
      if (hay) {
        const fields = [msg.table, authorName, msg.itemId, msg.change, msg.datemodified].map((f) => String(f).toLowerCase());

        // if none of them include our search substring, drop it:
        if (!fields.some((f) => f.includes(hay))) return false;
      }

      return true;
    });

    // 2) Sort newest first
    return filtered.sort((a, b) => {
      const da = new Date(JSON.parse(a.message).datemodified);
      const db = new Date(JSON.parse(b.message).datemodified);
      return db - da;
    });
  }, [auditLogsData, filterTable, filterAuthor, searchText]);

  return (
    <TooltipProvider>
      <Breadcrumb items={items} />
      <div className='row'>
        <div className='col-lg-12'>
          <section className='panel'>
            <aside className='profile-nav alt'>
              <section className='panel'>
                <header
                  className='panel-heading'
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    width: '100%',
                  }}>
                  Audit Logs
                  <div style={{ display: 'flex' }}>
                    {/* Period */}
                    <Select
                      options={periods}
                      styles={adminPortalSelect}
                      value={periods.find((p) => p.value === selectedPeriod)}
                      onChange={(o) => setSelectedPeriod(o.value)}
                      placeholder='Period'
                    />

                    {/* Table */}
                    <Select
                      isClearable
                      options={filterOptions.tables}
                      value={filterTable}
                      onChange={setFilterTable}
                      styles={adminPortalSelect}
                      placeholder='TABLE'
                    />

                    {/* Author */}
                    <Select
                      isClearable
                      options={filterOptions.authors}
                      value={filterAuthor}
                      onChange={setFilterAuthor}
                      styles={adminPortalSelect}
                      placeholder='AUTHOR'
                    />

                    {/* Global Search */}
                    <div style={{ marginLeft: '1rem' }}>
                      <input
                        type='text'
                        placeholder='Search…'
                        value={searchText}
                        onChange={(e) => setSearchText(e.target.value)}
                        style={{
                          padding: '6px 8px',
                          border: '1px solid #ccc',
                          borderRadius: '4px',
                          width: '160px',
                        }}
                      />
                    </div>
                  </div>
                </header>

                <div className='panel-body'>
                  <div className='row'>
                    <div className='col-lg-12'>
                      <div className='row'>
                        <div className='col-lg-12'>
                          <table className='app-table-striped'>
                            <thead>
                              <tr>
                                <th className='text-left p-[10px]'>Author</th>
                                <th className='text-left p-[10px]'>Date Modified</th>
                                <th className='text-left p-[10px]'>Change</th>
                                <th className='text-left p-[10px]'>Table</th>
                              </tr>
                            </thead>
                            <tbody>
                              {/* Map through the audit logs data */}
                              {displayedLogs.map((log) => {
                                let message = JSON.parse(log.message);
                                let diff = message.diff;
                                let change = message.change;
                                let table = message.table;
                                let datemodified = message.datemodified;
                                let itemId = message.itemId;

                                // Format date
                                let date = new Date(datemodified);
                                datemodified = date.toLocaleString();

                                // Split author into name and ID
                                let author = message.author;
                                let authorName = author.substring(0, author.indexOf('<'));
                                let start = author.indexOf('<');
                                let end = author.indexOf('>');
                                let authorID = author.substring(start + 1, end);

                                return (
                                  <tr key={log.eventId}>
                                    <td className='p-[10px]'>
                                      <div className='cursor-pointer admin_portal_cellData_description'>
                                        <Tooltip>
                                          <TooltipTrigger asChild>
                                            <p className='text-sm'>{authorName}</p>
                                          </TooltipTrigger>
                                          <TooltipContent>{authorID}</TooltipContent>
                                        </Tooltip>
                                      </div>
                                    </td>
                                    <td className='p-[10px]'>{datemodified}</td>
                                    <td className='p-[10px]'>
                                      {change === 'Added' && <span className='label label-success'>Added</span>}
                                      {change === 'Deleted' && <span className='label label-danger'>Deleted</span>}
                                      {change === 'Updated' && <span className='label label-warning'>Updated</span>}
                                    </td>
                                    <td className='p-[10px]'>{table}</td>
                                    <td className='p-[10px]'>
                                      <Button
                                        onClick={() => {
                                          setViewDetailsModalData({
                                            ...log,
                                            itemId,
                                            authorName,
                                            authorID,
                                            datemodified,
                                            diff,
                                            change,
                                            table,
                                          });
                                          setViewDetailsModal(true);
                                        }}>
                                        View Details
                                      </Button>
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  </div>
                  {/* Show loader */}
                  {showLoader && (
                    <div className='flex items-center justify-center pb-12'>
                      <Spinner size='large' />
                    </div>
                  )}
                </div>
              </section>
            </aside>
          </section>
        </div>
      </div>
      <ViewDetailsModal open={viewDetailsModal} handleClose={() => setViewDetailsModal(false)} data={viewDetailsModalData} />
    </TooltipProvider>
  );
};

const mapStateToProps = (state) => {
  return {
    auditLogs: state.admin?.auditLogs,
    showLoader: state.showLoader,
  };
};

const mapDispatchToProps = (dispatch) => ({
  getAuditLogs: (period) => dispatch(ADMINACTIONS.getAuditLogs(period)),
});

export default connect(mapStateToProps, mapDispatchToProps)(AuditLogs, ViewDetailsModal);
